import {Link, useCart, useUrl, Image} from '@shopify/hydrogen';
import {Customer} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';
import {useEffect, useState} from 'react';

import {
  AccountPopup,
  CartDrawer,
  NewMenuDrawer,
  MarketingBar,
  PostalSearchForm,
  useDrawerContext,
  CloseMenuIcon,
} from '~/components';
import {useScrollListener} from '~/hooks/index';
import menuIcon from '../../assets/menu.png';
import {TFR_ENV} from '~/utils/constants';
import {GetAnnouncementBarByIdResponse} from '~/graphql/strapi/homepageDataQuery';
import {ShoppingCartIcon} from '../icons/ShoppingCartIcon';

type Props = {
  customer: Customer;
  homepageData: {data: GetAnnouncementBarByIdResponse};
  menu: {
    title: string;
    to: string;
  }[];
};

type HeaderProps = {
  isHome?: boolean;
  openCart: () => void;
  openMenu: () => void;
  closeMenu: () => void;
  customer: Customer;
  homepageData: {data: GetAnnouncementBarByIdResponse};
  menu: {
    title: string;
    to: string;
  }[];
};

/**
 * A client component that specifies the content of the header on the website
 */
export function HeaderClient({customer, homepageData, menu}: Props) {
  const {isOpenCart, setIsOpenCart, isOpenMenu, setIsOpenMenu} =
    useDrawerContext();

  useEffect(() => {
    /*
  we are adding and removing overflow:hidden style here because: 
  1. when we open cart and we open menu as well without closing the cart the headlessui's dialog componet
   doesn't maintain adding or removing style in html tag properly so we need to maintain it ourself
  */
    const htmlElement = document.querySelector('html');
    if (!isOpenCart && !isOpenMenu) {
      setTimeout(() => {
        if (htmlElement) {
          htmlElement.style.removeProperty('overflow');
        }
      }, 1000);
    } else {
      setTimeout(() => {
        if (htmlElement) {
          htmlElement.style.overflow = 'hidden';
        }
      }, 1000);
    }
  }, [isOpenCart, isOpenMenu]);

  return (
    <>
      <CartDrawer isOpen={isOpenCart} onClose={() => setIsOpenCart(false)} />
      <NewMenuDrawer
        isOpen={isOpenMenu}
        onClose={() => setIsOpenMenu(false)}
        menu={menu}
        customer={customer}
      />
      <TopNavigation
        menu={menu}
        openCart={() => setIsOpenCart(true)}
        openMenu={() => setIsOpenMenu(true)}
        closeMenu={() => setIsOpenMenu(false)}
        customer={customer}
        homepageData={homepageData}
      />
    </>
  );
}

function MobileNavigation({
  openCart,
  openMenu,
  customer,
  closeMenu,
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const {isOpenMenu} = useDrawerContext();
  const {pathname} = useUrl();
  const scroll = useScrollListener();

  let isHome = false;
  if (pathname == '/') {
    isHome = true;
  }

  useEffect(() => {
    if (!isScrolled && window.scrollY > 400) {
      setIsScrolled(true);
    } else if (isScrolled && window.scrollY <= 400) {
      setIsScrolled(false);
    }
  }, [scroll.y, isScrolled]);

  return (
    <header
      role="banner"
      className="w-full border-b-2 border-clay-100 bg-foam-100 lg:hidden"
    >
      <div className="flex items-center justify-between px-6 py-5">
        <Link to="/">
          <Image
            src="/images/logo-full.png"
            height={16}
            width={156}
            alt="nav-logo"
          />
        </Link>
        <div className="flex items-center justify-between gap-2 md:gap-6">
          {/* <NavigationSearchIcon /> */}
          <AccountPopup
            isHome={isHome}
            isScrolled={isScrolled}
            customer={customer}
            position="bottom"
          />
          <button
            onClick={openCart}
            className="relative flex items-center justify-center"
          >
            <div className="inline-flex h-full w-full items-center justify-center self-center">
              <ShoppingCartIcon />
            </div>
            <CartBadge />
          </button>
          <button
            className="px-[6px]"
            onClick={() => (isOpenMenu ? closeMenu() : openMenu())}
          >
            {isOpenMenu ? (
              <CloseMenuIcon width={18} height={18} />
            ) : (
              <Image src={menuIcon} alt="..." width={18} height={18} />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function TopNavigation({
  openCart,
  openMenu,
  closeMenu,
  customer,
  homepageData,
  menu,
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const {isOpenMenu} = useDrawerContext();
  const {pathname} = useUrl();
  const scroll = useScrollListener();

  let isHome = false;
  if (pathname == '/') {
    isHome = true;
  }

  useEffect(() => {
    if (!isScrolled && window.scrollY > 400) {
      setIsScrolled(true);
    } else if (isScrolled && window.scrollY <= 400) {
      setIsScrolled(false);
    }
  }, [scroll.y, isScrolled]);

  return (
    <header
      role="banner"
      className={clsx(
        'fixed top-0 z-40 w-full bg-foam-100 duration-300',
        !isOpenMenu &&
          'border-b-0 border-transparent md:border-b-2 md:border-clay-100',
        isOpenMenu && 'border-b-0 border-transparent',
      )}
    >
      <MarketingBar homepageData={homepageData} />
      <MobileNavigation
        closeMenu={closeMenu}
        customer={customer}
        homepageData={homepageData}
        openCart={openCart}
        openMenu={openMenu}
        isHome={isHome}
        menu={menu}
      />
      <div className="flex justify-center px-4 py-3 sm:px-4 md:justify-between md:px-8 lg:flex lg:px-16">
        <div className="hidden items-center gap-4 lg:flex">
          <Link to="/">
            <Image
              src="/images/logo-full.png"
              height={16}
              width={156}
              alt="nav-logo"
            />
          </Link>
          <div className="hidden space-x-4 sm:-my-px sm:ml-6 sm:flex">
            {menu?.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={clsx(
                  item.to === pathname
                    ? 'border-daybreak-400 text-daybreak-400'
                    : 'border-transparent hover:text-daybreak-400',
                  'inline-flex items-center border-b-2 px-1 pt-1 text-acai-900',
                )}
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex w-full justify-center lg:hidden">
          <PostalSearchForm containerClassName="w-[320px]" />
        </div>
        <div className="hidden items-center justify-end gap-3 lg:flex">
          {TFR_ENV !== 'production' && (
            <h3 className="capitalize text-orange-500">DEV</h3>
          )}
          <PostalSearchForm containerClassName="hidden md:block md:w-[300px]" />

          <AccountPopup
            isHome={isHome}
            isScrolled={isScrolled}
            customer={customer}
            position="top"
          />
          <button
            onClick={openCart}
            className="relative flex h-8 w-8 items-center justify-center focus:ring-primary/5"
          >
            <div className="inline-flex h-full w-full items-center justify-center self-center">
              <ShoppingCartIcon />
            </div>
            <CartBadge />
          </button>
        </div>
      </div>
    </header>
  );
}

function CartBadge() {
  const {lines} = useCart();

  const nonFeeCount = lines.filter(
    (l) => !l.attributes.find(({key}) => key === '_for_tfr_line_id'),
  ).length;

  if (nonFeeCount < 1) {
    return null;
  }

  return (
    <div className="absolute bottom-0 right-0 flex aspect-square h-4 w-auto min-w-[0.75rem] items-center justify-center rounded-full bg-acai-900 px-[0.125rem] pb-px text-center text-[0.625rem] font-medium leading-none subpixel-antialiased">
      <span className="text-white">{nonFeeCount}</span>
    </div>
  );
}
